import { useMutation } from '@apollo/client';
import { transformError } from '@core/adapters/errors/transformError';
import { track } from '@core/logging';
import { EXTENDED_GRAPHQL_TIMEOUT_MS } from '@core/services/constants';
import { useCallback } from 'react';

import { GENERATE_AUDIT_REPORT } from '../graphql/mutations/generateAuditReport';

interface GenerateAuditReportResponse {
  generateAuditReport: {
    id: string;
    createdAt: string;
    createdBy: string;
    metrics: {
      overall: {
        precision: number;
        recall: number;
        f1Score: number;
        accuracy: number;
      };
      labelComparison: {
        exactMatchCount: number;
        exactMatchPercentage: number;
        additionalLabelsCount: number;
        additionalLabelsPercentage: number;
        labelsMissingCount: number;
        labelsMissingPercentage: number;
        bothMissingAndAdditionalCount: number;
        bothMissingAndAdditionalPercentage: number;
      };
      byLabel: Array<{
        label: string;
        metrics: {
          precision: number;
          recall: number;
          f1Score: number;
          accuracy: number;
        };
      }>;
    };
  };
}

interface GenerateAuditReportVariables {
  sessionId: string;
}

/**
 * Hook for generating an audit report
 * Follows established audit domain patterns for error handling and business event tracking
 */
export function useGenerateAuditReport() {
  const [generateAuditReportMutation, { loading }] = useMutation<GenerateAuditReportResponse, GenerateAuditReportVariables>(GENERATE_AUDIT_REPORT, {
    errorPolicy: 'all',
    context: { timeout: EXTENDED_GRAPHQL_TIMEOUT_MS },
  });

  const generateAuditReport = useCallback(
    async (auditSessionId: string) => {
      // Track audit report generation initiation
      track('GenerateAuditReportInitiated', {
        sessionId: auditSessionId,
      });

      try {
        const result = await generateAuditReportMutation({
          variables: {
            sessionId: auditSessionId,
          },
        });

        if (result.errors) {
          const adaptedErrors = result.errors.map(error => transformError({ graphQLErrors: [error] }));

          // Track audit report generation failure
          track('GenerateAuditReportFailed', {
            sessionId: auditSessionId,
            errorType: 'api_error',
            errorMessage: adaptedErrors[0]?.message || 'Unknown error',
          });

          return {
            success: false,
            errors: adaptedErrors,
          };
        }

        // Track successful audit report generation
        if (result.data?.generateAuditReport) {
          track('GenerateAuditReportSucceeded', {
            sessionId: auditSessionId,
            reportId: result.data.generateAuditReport.id,
          });
        }

        return {
          success: true,
          data: result.data?.generateAuditReport,
        };
      } catch (error) {
        const adaptedError = transformError(error);

        // Track unexpected audit report generation failure
        track('GenerateAuditReportFailed', {
          sessionId: auditSessionId,
          errorType: error instanceof Error ? error.name : 'unknown',
          errorMessage: error instanceof Error ? error.message : 'Unexpected error',
        });

        return {
          success: false,
          errors: [adaptedError],
        };
      }
    },
    [generateAuditReportMutation]
  );

  return {
    generateAuditReport,
    loading,
  };
}
